export const MERGE_BLOCKING_RESTRICTIONS = [
  "removable",
  "textEdit",
  "textBoundary"
];

export interface TextBoxLike {
  uniqueId: string;
  type?: string;
  value?: string;
  top?: number;
  left?: number;
  width?: number;
  height?: number;
}

export interface BoundingBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const determineMasterTextBox = <T extends TextBoxLike>(
  textBoxes: T[]
): T => {
  if (!textBoxes || textBoxes.length === 0) {
    throw new Error("No text box elements provided");
  }

  return textBoxes.reduce((master, current) => {
    const currentTop = current.top ?? 0;
    const masterTop = master.top ?? 0;
    const currentLeft = current.left ?? 0;
    const masterLeft = master.left ?? 0;

    if (currentTop < masterTop) {
      return current;
    }
    if (currentTop === masterTop) {
      return currentLeft < masterLeft ? current : master;
    }
    return master;
  });
};

export const sortTextBoxesInReadingOrder = <T extends TextBoxLike>(
  textBoxes: T[]
): T[] =>
  [...textBoxes].sort((a, b) => {
    const aTop = a.top ?? 0;
    const bTop = b.top ?? 0;
    if (aTop !== bTop) return aTop - bTop;
    const aLeft = a.left ?? 0;
    const bLeft = b.left ?? 0;
    return aLeft - bLeft;
  });

export const mergeTextBoxValues = <T extends TextBoxLike>(
  textBoxesInOrder: T[]
): string =>
  textBoxesInOrder
    .map(box => box.value ?? "")
    .filter(value => value !== "")
    .join("<br />");

export const computeMergedBoundingBox = <T extends TextBoxLike>(
  textBoxes: T[]
): BoundingBox => {
  if (!textBoxes || textBoxes.length === 0) {
    throw new Error("No text box elements provided");
  }

  let minTop = Infinity;
  let minLeft = Infinity;
  let maxBottom = -Infinity;
  let maxRight = -Infinity;

  textBoxes.forEach(box => {
    const top = box.top ?? 0;
    const left = box.left ?? 0;
    const width = box.width ?? 0;
    const height = box.height ?? 0;
    if (top < minTop) minTop = top;
    if (left < minLeft) minLeft = left;
    if (top + height > maxBottom) maxBottom = top + height;
    if (left + width > maxRight) maxRight = left + width;
  });

  return {
    top: minTop,
    left: minLeft,
    width: maxRight - minLeft,
    height: maxBottom - minTop
  };
};
